<template>
  <div class="border-b py-4 last:border-b-0">
    <!-- Item principal -->
    <div class="flex gap-4">
      <!-- Image -->
      <div class="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0">
        <ImgComponent
          :src="item.dishImage"
          class="w-full h-full object-cover"
          alt=""
        />
      </div>

      <!-- Infos + Contrôles -->
      <div class="flex-1">
        <div class="flex items-center gap-2">
          <div class="font-bold text-gray-800">{{ item.dishName }}</div>
          <!-- Promotion badge -->
          <div
            v-if="item.promotion"
            class="px-2 py-0.5 rounded-full text-xs font-bold bg-primary text-white shadow-sm"
          >
            <template v-if="item.promotion.discountType === 'percentage'">
              -{{ item.promotion.discount }}%
            </template>
            <template v-else-if="item.promotion.discountType === 'fixed'">
              -{{ item.promotion.discount }} F
            </template>
          </div>
        </div>
        <div class="text-sm text-gray-600">
          {{ formatOptionsSummary(item.options) }}
        </div>
        <div v-if="item.drinks?.length" class="text-sm text-gray-600">
          + {{ formatDrinksSummary(item.drinks) }}
        </div>
        <div class="flex items-end gap-2 mt-1 -mb-1">
          <div class="font-bold text-lg text-primary flex items-center gap-2">
            <span>{{ Math.round(item.unitPrice) }} FCFA</span>
          </div>
          <span
            v-if="item.promotion && item.originalPrice"
            class="line-through text-gray-400 text-xs mb-1 font-medium"
          >
            {{ item.originalPrice }} FCFA
          </span>
        </div>
      </div>

      <div class="flex flex-col items-center gap-2">
        <CartItemControls
          :quantity="item.quantity"
          @increase="$emit('increase')"
          @decrease="$emit('decrease')"
        />
        <button
          @click="$emit('remove')"
          class="text-red-500 text-sm hover:text-red-700"
        >
          Supprimer
        </button>
      </div>
    </div>

    <!-- Bouton pour expand options/boissons -->
    <button
      v-if="hasOptionsOrDrinks"
      @click="expanded = !expanded"
      class="w-full mt-3 px-4 py-2 bg-gray-50 rounded-xl text-sm text-gray-700 flex items-center justify-between hover:bg-gray-100"
    >
      <span>Options & boissons</span>
      <ImgComponent
        src="/images/all/chevron-down.svg"
        class="w-4 h-4 transition-transform"
        :class="{ 'rotate-180': expanded }"
      />
    </button>

    <!-- Section extensible options/boissons -->
    <Transition name="expand">
      <div v-if="expanded" class="mt-3 space-y-3">
        <!-- Options sélectionnées (MODIFIABLE) -->
        <div v-if="item.options?.length" class="rounded-xl border overflow-hidden">
          <div class="bg-slate-100 px-4 py-2 text-sm font-semibold">
            Options
          </div>
          <CartOptionsEditor
            :itemId="item.id"
            :dishId="item.dishId"
            :selectedOptions="item.options"
            @update="$emit('updateOptions', { itemId: item.id, options: $event })"
          />
        </div>

        <!-- Boissons sélectionnées (MODIFIABLE) -->
        <div class="rounded-xl border overflow-hidden">
          <div class="bg-slate-100 px-4 py-2 text-sm font-semibold">
            Boissons
          </div>
          <CartDrinksEditor
            :itemId="item.id"
            :restaurantId="restaurantId"
            :selectedDrinks="item.drinks || []"
            @update="$emit('updateDrinks', { itemId: item.id, drinks: $event })"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import ImgComponent from '@ui/core/img/ImgComponent.global.vue';
import CartItemControls from './CartItemControls.global.vue';
import CartOptionsEditor from './CartOptionsEditor.global.vue';
import CartDrinksEditor from './CartDrinksEditor.global.vue';

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  restaurantId: {
    type: String,
    required: true
  }
});

defineEmits([
  'increase',
  'decrease',
  'remove',
  'updateOptions',
  'updateDrinks'
]);

const expanded = ref(false);

const hasOptionsOrDrinks = computed(() => {
  return (props.item.options?.length > 0) || (props.item.drinks?.length > 0);
});

const formatOptionsSummary = (options) => {
  if (!options?.length) return 'Aucune option';
  return options.map(opt => opt.valueName || opt.value).join(', ');
};

const formatDrinksSummary = (drinks) => {
  if (!drinks?.length) return '';
  return drinks.map(d => `${d.name} x${d.quantity}`).join(', ');
};
</script>

<style scoped>
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
